import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { GET_ACCOUNT } from 'connectors/queries/account';
import { notifyCommerceIncomplete, notifyInvalidBillToAddress } from 'connectors/bugsnag';
import { accountState } from 'state/atoms';
import { getGraphqlOperation } from 'utils/helpers';
import { getAuthData } from 'connectors/authentication';
import { defaultCurrentRmaState, defaultCheckoutState } from 'state/defaults';
import { storageCheckoutSelector, storageCurrentRmaSelector } from 'state/selectors';

// Run validations and load the account data into the state
export const useAccountLoad = ({ accountId, endCustomerAccountId }) => {
  const [validation, setValidation] = useState({
    commerceComplete: true,
    hasBillToAddress: true,
  });
  const setAccount = useSetRecoilState(accountState);
  const [currentRma, setCurrentRma] = useRecoilState(storageCurrentRmaSelector);
  const setStorageCheckout = useSetRecoilState(storageCheckoutSelector);

  const variables = { accountId, endCustomerAccountId };

  const onRequestCompleted = (data) => {
    const account = data ? data.getAccount : {};

    const validationResults = {
      ...validation,
      commerceComplete: account.commerceComplete,
    };

    if (!validationResults.commerceComplete) {
      notifyCommerceIncomplete();
    }

    setValidation(validationResults);
    setAccount({ ...account });

    if (account.defaultCurrencyCode !== currentRma.currencyCode) {
      const localStorageRmaData = {
        accountId: getAuthData().endCustomerAccountId,
        currencyCode: account.defaultCurrencyCode,
      };
      const localStorageCheckoutData = {};

      setCurrentRma({ ...defaultCurrentRmaState, ...localStorageRmaData });
      setStorageCheckout({ ...defaultCheckoutState, ...localStorageCheckoutData });
    }
  };

  const onRequestError = (response) => {
    setValidation({
      ...validation,
      hasBillToAddress: false,
    });

    // Don't report to bugsnag in case of network error
    response.graphQLErrors.length &&
      notifyInvalidBillToAddress({
        response,
        operation: getGraphqlOperation({
          query: GET_ACCOUNT,
          variables,
        }),
      });
  };

  const isMissingAccountInfo = !variables.accountId || !variables.endCustomerAccountId;

  const request = useQuery(GET_ACCOUNT, {
    variables,
    skip: isMissingAccountInfo,
    onCompleted: onRequestCompleted,
    onError: onRequestError,
  });

  return [validation, request];
};
